import { useRouter } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ENTITY_TYPE_NAMES,
  NameIndex,
  ignoreKeyForCandidates,
  ignoreKeyForName,
  linkSceneHtml,
  scanSceneHtml,
  unlinkSceneHtml,
  withoutIgnored,
  type Candidate,
  type EntrySummary,
  type IgnoredMention,
  type Occurrence,
  type UnknownName,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { settings } from '../settings.ts';

/**
 * Scan for Connections (docs/PARITY-STEP-5-PLAN.md section 5), as the desktop's
 * `ConnectionScanDialog`: what the scene's text mentions, grouped by the entry it means,
 * each group to link in the text (the checked mentions), add as a reference only, or
 * ignore here; a name that could mean several entries asks which one. Links already in
 * the text can be removed, and names with no entry made into entries on the spot, after
 * which they're scanned like any other. What's ignored is kept in the realm, for this scene.
 */

type Action = 'link' | 'reference' | 'ignore';

interface Group {
  readonly key: string;
  readonly candidates: readonly Candidate[];
  readonly occurrences: readonly Occurrence[];
}

interface GroupChoice {
  readonly action: Action;
  /** The entry it means: set at once when there's only one. */
  readonly entryId: string | null;
  /** Occurrence numbers left unchecked. */
  readonly skipped: ReadonlySet<number>;
}

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);
const TYPES = Object.keys(ENTITY_TYPE_NAMES).filter((type) => type !== 'CUSTOM');

export function SceneScan({
  file,
  sceneId,
  html,
  references,
  entries,
  onApply,
  onCancel,
}: {
  file: string;
  sceneId: string;
  html: string;
  references: readonly string[];
  entries: readonly EntrySummary[];
  onApply: (html: string, referenceIds: readonly string[]) => void;
  onCancel: () => void;
}) {
  const router = useRouter();
  const [ignored, setIgnored] = useState<readonly IgnoredMention[]>([]);
  const [choices, setChoices] = useState<ReadonlyMap<string, GroupChoice>>(new Map());
  const [unlinks, setUnlinks] = useState<ReadonlySet<string>>(new Set());
  const [addReferences, setAddReferences] = useState(true);
  const [types, setTypes] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState('');

  const loadIgnored = () =>
    realms.ignoredMentions(file, sceneId).then(setIgnored, (e: unknown) => setError(message(e, 'What was ignored here could not be read.')));
  useEffect(() => {
    void loadIgnored();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, sceneId]);

  const index = useMemo(() => NameIndex.of(entries), [entries]);
  const scan = useMemo(() => withoutIgnored(scanSceneHtml(html, index), ignored.map((i) => i.key)), [html, index, ignored]);
  const groups = useMemo(() => {
    const byKey = new Map<string, { key: string; candidates: readonly Candidate[]; occurrences: Occurrence[] }>();
    for (const occurrence of scan.occurrences) {
      const key = occurrence.candidates.map((c) => c.entryId).join(',');
      let group = byKey.get(key);
      if (!group) {
        group = { key, candidates: occurrence.candidates, occurrences: [] };
        byKey.set(key, group);
      }
      group.occurrences.push(occurrence);
    }
    return [...byKey.values()] as Group[];
  }, [scan]);

  const choiceOf = (group: Group): GroupChoice =>
    choices.get(group.key) ?? { action: 'link', entryId: group.candidates.length === 1 ? group.candidates[0]!.entryId : null, skipped: new Set() };
  const choose = (group: Group, change: Partial<GroupChoice>) => setChoices(new Map(choices).set(group.key, { ...choiceOf(group), ...change }));
  const title = (id: string) => entries.find((e) => e.id === id)?.title ?? '(a deleted entry)';
  const label = (group: Group) => [...new Set(group.candidates.map((c) => c.title))].join(' / ');

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  const ignoreName = (name: UnknownName) =>
    run(async () => {
      await realms.ignoreMention(file, sceneId, ignoreKeyForName(name.name), name.name);
      await loadIgnored();
    }, 'The name could not be ignored.');
  const stopIgnoring = (key: string) =>
    run(async () => {
      await realms.stopIgnoringMention(file, sceneId, key);
      await loadIgnored();
    }, 'That could not be put back.');
  const createEntry = (name: UnknownName) =>
    run(async () => {
      await realms.createEntry(file, { title: name.name, entityType: types.get(name.name) ?? name.type, audience: { role: settings()['new.entryAudience'], customLabel: '' } });
      // The realm's entries reload, and the scan with them: the new entry is found like any other.
      await router.invalidate();
    }, 'The entry could not be made.');

  const apply = () =>
    run(async () => {
      const toLink = new Map<number, string>();
      const referenceIds = new Set<string>();
      for (const group of groups) {
        const choice = choiceOf(group);
        if (choice.action === 'ignore') {
          // Remembered for this scene, so the next scan doesn't ask again.
          await realms.ignoreMention(file, sceneId, ignoreKeyForCandidates(group.candidates), label(group));
          continue;
        }
        if (choice.entryId === null) continue;
        if (choice.action === 'reference') {
          referenceIds.add(choice.entryId);
          continue;
        }
        let any = false;
        for (const occurrence of group.occurrences) {
          if (choice.skipped.has(occurrence.index)) continue;
          toLink.set(occurrence.index, choice.entryId);
          any = true;
        }
        if (any && addReferences) referenceIds.add(choice.entryId);
      }
      // Linking reads the text as the scan did, so the numbers still hold; then the unlinks.
      let updated = toLink.size === 0 ? html : linkSceneHtml(html, index, toLink);
      for (const id of unlinks) updated = unlinkSceneHtml(updated, id);
      onApply(updated, [...referenceIds]);
    }, 'The connections could not be applied.');

  const mentions = scan.occurrences.length;
  const unknown = scan.unknownNames.length;
  return (
    <section className="panel scene-scan" aria-label="Scan for Connections">
      <h3>Scan for Connections</h3>
      <p className="meta" role="status">
        {mentions === 0 && unknown === 0
          ? 'No new connections found.'
          : `${mentions} ${mentions === 1 ? 'mention' : 'mentions'} of the realm's lore${unknown === 0 ? '' : `, and ${unknown} ${unknown === 1 ? 'name' : 'names'} with no entry yet`}. Choose what to link.`}
      </p>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      {groups.length > 0 && <h4>Mentioned in the text</h4>}
      {groups.map((group) => {
        const choice = choiceOf(group);
        const found = [...new Set(group.occurrences.map((o) => `“${o.text}”`))].join(', ') + (group.occurrences.length > 1 ? ` ×${group.occurrences.length}` : '');
        const name = label(group);
        return (
          <fieldset key={group.key} className="scan-card">
            <legend>{name}</legend>
            <div className="scan-head">
              {group.candidates.length === 1 ? (
                <span className="chip">{ENTITY_TYPE_NAMES[group.candidates[0]!.entityType] ?? group.candidates[0]!.entityType}</span>
              ) : (
                <label>
                  Which one does this mean?
                  <select value={choice.entryId ?? ''} onChange={(e) => choose(group, { entryId: e.currentTarget.value || null })}>
                    <option value="">Choose…</option>
                    {group.candidates.map((c) => (
                      <option key={c.entryId} value={c.entryId}>
                        {c.title} ({ENTITY_TYPE_NAMES[c.entityType] ?? c.entityType})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <span className="meta">found as {found}</span>
              <label>
                <span className="visually-hidden">What to do with {name}</span>
                <select value={choice.action} onChange={(e) => choose(group, { action: e.currentTarget.value as Action })}>
                  <option value="link">Link in the text</option>
                  <option value="reference">Add as a reference only</option>
                  <option value="ignore">Ignore</option>
                </select>
              </label>
            </div>
            {group.occurrences.map((o) => (
              <label key={o.index} className="check scan-occurrence">
                <input
                  type="checkbox"
                  disabled={choice.action !== 'link'}
                  checked={!choice.skipped.has(o.index)}
                  onChange={(e) => {
                    const skipped = new Set(choice.skipped);
                    if (e.currentTarget.checked) skipped.delete(o.index);
                    else skipped.add(o.index);
                    choose(group, { skipped });
                  }}
                />
                <span>
                  <span className="scan-context">{o.before}</span>
                  <mark>{o.text}</mark>
                  <span className="scan-context">{o.after}</span>
                </span>
              </label>
            ))}
            {choice.entryId !== null && references.includes(choice.entryId) && <p className="meta">Already one of this scene's references.</p>}
          </fieldset>
        );
      })}

      {scan.linkedEntryIds.length > 0 && (
        <>
          <h4>Already linked in the text</h4>
          <ul className="story-children">
            {scan.linkedEntryIds.map((id) => (
              <li key={id}>
                <span>{title(id)}</span>
                <span className="story-child-tools">
                  <button
                    type="button"
                    className="quiet"
                    aria-pressed={unlinks.has(id)}
                    onClick={() => {
                      const next = new Set(unlinks);
                      if (!next.delete(id)) next.add(id);
                      setUnlinks(next);
                    }}
                  >
                    {unlinks.has(id) ? 'Will unlink' : 'Unlink'}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {scan.unknownNames.length > 0 && <h4>Names with no entry yet</h4>}
      {scan.unknownNames.map((name) => (
        <div key={name.name} className="scan-card" role="group" aria-label={name.name}>
          <div className="scan-head">
            <strong>
              {name.name}
              {name.count > 1 ? ` ×${name.count}` : ''}
            </strong>
            <label>
              <span className="visually-hidden">Type for {name.name}</span>
              <select value={types.get(name.name) ?? name.type} onChange={(e) => setTypes(new Map(types).set(name.name, e.currentTarget.value))}>
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {ENTITY_TYPE_NAMES[type]}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" onClick={() => void createEntry(name)}>
              Create entry
            </button>
            <button type="button" className="quiet" title="Don't suggest this name here again" onClick={() => void ignoreName(name)}>
              Ignore
            </button>
          </div>
          <p className="meta">
            {name.before}
            <mark>{name.name}</mark>
            {name.after}
          </p>
        </div>
      ))}

      {ignored.length > 0 && (
        <>
          <h4>Ignored here</h4>
          <p className="meta">Left out of this scan because you ignored them before.</p>
          <p className="chips">
            {ignored.map((i) => (
              <span key={i.key} className="chip">
                {i.label.trim() === '' ? '(unnamed)' : i.label}{' '}
                <button type="button" className="link-button" onClick={() => void stopIgnoring(i.key)}>
                  Stop ignoring
                </button>
              </span>
            ))}
          </p>
        </>
      )}

      <label className="check">
        <input type="checkbox" checked={addReferences} onChange={(e) => setAddReferences(e.currentTarget.checked)} />
        Also add linked entries to this scene's characters, locations and lore
      </label>
      <div className="actions">
        <button type="button" className="primary" onClick={() => void apply()}>
          Apply
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
